import type { Question } from '@exam-management/graphql';
import { ErrorBox, Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';

import { useGetInDevelopmentSingleQuestionById } from '../../hooks';

export interface SingleQuestionDetailsContextData {
  singleQuestion: Question;
}

interface SingleQuestionDetailsProviderProps extends PropsWithChildren {
  taskId: string;
  questionId: string;
}

export const SingleQuestionDetailsContext = createContext<
  SingleQuestionDetailsContextData | undefined
>(undefined);

export default function SingleQuestionDetailsProvider(
  props: Readonly<SingleQuestionDetailsProviderProps>,
) {
  const { taskId, questionId, children } = props;

  const {
    data: singleQuestionData,
    loading: isLoading,
    error: getQuestionError,
  } = useGetInDevelopmentSingleQuestionById({
    questionId,
    taskId,
  });

  const singleQuestionDetails = singleQuestionData as Question;

  const contextValue = useMemo(() => {
    return {
      singleQuestion: singleQuestionDetails,
    };
  }, [singleQuestionDetails]);

  if (isLoading || !singleQuestionDetails) {
    return <Loading sx={{ height: '100%' }} />;
  }

  if (getQuestionError) {
    return <ErrorBox />;
  }

  return (
    <SingleQuestionDetailsContext.Provider value={contextValue}>
      {children}
    </SingleQuestionDetailsContext.Provider>
  );
}
